/// <amd-module name="Build/Admin/Common/Components/JobsList" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { onClickFPS } from "VSS/Platform/FPS";
import { VssComponent } from "VSS/Platform/Layout";

import { Ago } from "VSSUI/Ago";
import { Duration } from "VSSUI/Duration";
import { Link, ILinkProps } from "VSSUI/Link";
import { Status, Statuses, IStatusProps, StatusSize } from "VSSUI/Status";
import { Table, ITableColumn, SimpleTableCell, TableColumnStyle, ITableRow, TwoLineTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { AgoFormat, tooltipString } from "VSSUI/Utilities/Date";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

import { TaskAgentJobRequest, TaskResult } from "DistributedTask/Client/Contracts/TaskAgent";

import { ITeamProject } from "TFS/Services/TfsProjectService";

import { IPoolAdminLinkingService } from "Build/Admin/Common/Services/PoolAdminLinkingService";
import { SignalRState } from "Build/Admin/Common/Stores/SignalRStore";
import { getAgentSpecificationName, getJobNumber, getProjectName } from "Build/Admin/Common/TaskAgentJobRequestUtils";

import * as Resources from "Build/Admin/Common/Resources";

/**
 * Controls the behavior of the "agent" column
 */
export const enum AgentColumnStyle {
    /**
     * Hide the agent column
     */
    None = 1,

    /**
     * Display the name of the agent that the job ran on
     */
    Agent = 2,

    /**
     * Display the Agent Specification that the job ran on
     */
    AgentSpec = 3
}

export interface IJobsListProps {
    agentColumnStyle: AgentColumnStyle;
    agentLinkProvider?(job: TaskAgentJobRequest): string | undefined;
    jobs: TaskAgentJobRequest[];
    projects: ITeamProject[];
    signalRState: SignalRState;
}

class TableListItem {
    value: TaskAgentJobRequest;
    linkProps: ILinkProps;
}

export class JobsList extends VssComponent<IJobsListProps> {
    public render(): JSX.Element {
        return (
            <Table<TableListItem>
                className="pipelines-jobs-list"
                columns={this._getColumns()}
                itemProvider={new ArrayItemProvider<TableListItem>(this._buildListItems())}
                onActivate={this._onRowClicked}
            />
        );
    }

    private _buildListItems(): TableListItem[] {
        const tableItems: TableListItem[] = [];

        if (this.props.jobs) {
            this.props.jobs.forEach(job => {
                const item = {
                    value: job
                } as TableListItem;

                if (job.owner) {
                    const linkingService = this.context.pageContext.getService<IPoolAdminLinkingService>("IPoolAdminLinkingService");
                    item.linkProps = { href: linkingService.getJobResultsUrl(job) } as ILinkProps;
                }

                tableItems.push(item);
            });
        }

        return tableItems;
    }

    private _getColumns(): ITableColumn<TableListItem>[] {
        // If we hide the agent column, evenly distribute the agent column width across the other columns
        const nameColumnWidth: number = this.props.agentColumnStyle === AgentColumnStyle.None ? -38 : -35;
        const projectColumnWidth: number = this.props.agentColumnStyle === AgentColumnStyle.None ? -18 : -15;
        const queuedColumnWidth: number = this.props.agentColumnStyle === AgentColumnStyle.None ? -18 : -15;
        const waitColumnWidth: number = this.props.agentColumnStyle === AgentColumnStyle.None ? -13 : -10;
        const durationColumnWidth: number = this.props.agentColumnStyle === AgentColumnStyle.None ? -13 : -10;

        const columns: ITableColumn<TableListItem>[] = [
            {
                id: "name",
                name: Resources.Name,
                columnStyle: TableColumnStyle.Primary,
                width: new ObservableValue(nameColumnWidth),
                renderCell: this._renderNameCell
            } as ITableColumn<TableListItem>,
            {
                id: "project",
                name: Resources.Project,
                columnStyle: TableColumnStyle.Secondary,
                width: new ObservableValue(projectColumnWidth),
                renderCell: this._renderProjectCell
            } as ITableColumn<TableListItem>
        ];

        if (this.props.agentColumnStyle === AgentColumnStyle.Agent) {
            columns.push(
                {
                    id: "agent",
                    name: Resources.Agent,
                    columnStyle: TableColumnStyle.Tertiary,
                    width: new ObservableValue(-15),
                    renderCell: this._renderAgentCell
                } as ITableColumn<TableListItem>
            );
        }
        else if (this.props.agentColumnStyle === AgentColumnStyle.AgentSpec) {
            columns.push(
                {
                    id: "agentSpec",
                    name: Resources.AgentSpecification,
                    columnStyle: TableColumnStyle.Tertiary,
                    width: new ObservableValue(-15),
                    renderCell: this._renderAgentSpecCell
                } as ITableColumn<TableListItem>
            );
        }

        columns.push(
            {
                id: "queued",
                name: Resources.Queued,
                columnStyle: TableColumnStyle.Tertiary,
                width: new ObservableValue(queuedColumnWidth),
                renderCell: this._renderQueuedTimeCell
            } as ITableColumn<TableListItem>,
            {
                id: "waitTime",
                name: Resources.WaitTime,
                columnStyle: TableColumnStyle.Tertiary,
                width: new ObservableValue(waitColumnWidth),
                renderCell: this._renderWaitTimeCell
            } as ITableColumn<TableListItem>,
            {
                id: "duration",
                name: Resources.Duration,
                columnStyle: TableColumnStyle.Tertiary,
                width: new ObservableValue(durationColumnWidth),
                renderCell: this._renderDurationCell
            } as ITableColumn<TableListItem>
        );

        return columns;
    }

    private _onRowClicked = (event: React.SyntheticEvent<HTMLElement>, row: ITableRow<TableListItem>): void => {
        if (row.data.linkProps && row.data.linkProps.href) {
            onClickFPS(this.context.pageContext, row.data.linkProps.href, true, event);
        }
    }

    private _renderNameCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        return (
            <TwoLineTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={"col-" + columnIndex}
                line1={this._renderPrimaryNameRowText(tableItem.value)}
                line2={this._renderSecondaryNameRowText(tableItem.value)}
                iconProps={{
                    render: (className?: string | undefined): JSX.Element => this._renderJobStatusIcon(tableItem.value, className)
                }}
            />
        );
    }

    private _renderJobStatusIcon(job: TaskAgentJobRequest, className?: string | undefined): JSX.Element {
        return <Status {...this._getStatusPropsForJob(job)} className={className} size={StatusSize.l} />;
    }

    private _renderPrimaryNameRowText(job: TaskAgentJobRequest): JSX.Element {
        return (
            <Tooltip overflowOnly={true}>
                <span className="fontWeightSemiBold text-ellipsis bolt-list-cell-text link-left-padding">
                    {getJobNumber(job)}
                </span>
            </Tooltip>
        );
    }

    private _renderSecondaryNameRowText(job: TaskAgentJobRequest): JSX.Element {
        return (
            <div className="fontSize secondary-text flex-row flex-baseline">
                {this._getJobNameContent(job)}
                {this._getPipelineNameContent(job)}
            </div>
        );
    }

    private _getJobNameContent(job: TaskAgentJobRequest): JSX.Element {
        let href: string | undefined;
        let name: string = Resources.TaskAgentJobListUnknown;

        if (job.owner) {
            if (job.jobName) {
                name = format(Resources.TaskAgentJobNameFormat, job.owner.name, job.jobName);
            }
            else {
                name = job.owner.name;
            }

            if (job.owner._links && job.owner._links.web) {
                href = job.owner._links.web.href;
            }
        }

        if (href) {
            return <Link className="text-ellipsis" href={href} subtle={true}>{name}</Link>;
        }
        else {
            return <span className="text-ellipsis link-padding">{name}</span>;
        }
    }

    private _getStatusPropsForJob(job: TaskAgentJobRequest): IStatusProps {
        if (job.finishTime) {
            switch (job.result) {
                case TaskResult.Failed:
                    return Statuses.Failed;
                case TaskResult.Abandoned:
                case TaskResult.Canceled:
                    return Statuses.Canceled;
                case TaskResult.Skipped:
                    return Statuses.Skipped;
                case TaskResult.SucceededWithIssues:
                    return Statuses.Warning;
                case TaskResult.Succeeded:
                default:
                    return Statuses.Success;
            }
        }
        else if (job.receiveTime) {
            return Statuses.Running;
        }
        else {
            return Statuses.Queued;
        }
    }

    private _getPipelineNameContent(job: TaskAgentJobRequest): JSX.Element {
        if (job.definition) {
            if (job.definition._links && job.definition._links.web) {
                return <Link className="text-ellipsis" href={job.definition._links.web.href} subtle={true}>{job.definition.name}</Link>;
            }
            else {
                return <span className="text-ellipsis bolt-list-cell-text link-padding">{job.definition.name}</span>;
            }
        }
        else {
            return <span className="text-ellipsis bolt-list-cell-text link-padding">{Resources.TaskAgentJobListUnknown}</span>;
        }
    }

    private _renderProjectCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <Tooltip overflowOnly={true}>
                    <span className="text-ellipsis bolt-list-cell-text">
                        {getProjectName(tableItem.value, this.props.projects)}
                    </span>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private _renderAgentCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        const name: string = tableItem.value.reservedAgent ? tableItem.value.reservedAgent.name : "";
        const agentLink: string | undefined = this.props.agentLinkProvider ? this.props.agentLinkProvider(tableItem.value) : undefined;

        let agentCellContents: JSX.Element;
        if (agentLink) {
            agentCellContents =
                <Link
                    className="text-ellipsis"
                    onClick={(ev) => onClickFPS(this.context.pageContext, agentLink, true, ev)}
                    subtle={true}>
                    {name}
                </Link>
            ;
        }
        else {
            agentCellContents = <span className="text-ellipsis bolt-list-cell-text">{name}</span>;
        }

        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <Tooltip overflowOnly={true}>
                    {agentCellContents}
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private _renderAgentSpecCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <Tooltip overflowOnly={true}>
                    <span className="text-ellipsis bolt-list-cell-text">
                        {getAgentSpecificationName(tableItem.value)}
                    </span>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private _renderQueuedTimeCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <span className="text-ellipsis bolt-list-cell-text">
                {
                    tableItem.value.queueTime &&
                    <Ago date={tableItem.value.queueTime} format={AgoFormat.Extended} />
                }
                </span>
            </SimpleTableCell>
        );
    }

    private _renderWaitTimeCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        // Cancelled jobs will have no assign time, but we still want the duration to have an end point in that scenario
        const endTime = tableItem.value.assignTime || tableItem.value.finishTime;

        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <span className="text-ellipsis bolt-list-cell-text">
                {
                    tableItem.value.queueTime &&
                    <Duration
                        startDate={tableItem.value.queueTime}
                        endDate={this.props.signalRState === SignalRState.Disconnected && !endTime ? new Date(Date.now()) : endTime}
                        tooltipProps={{ text: format(Resources.QueuedAtTime, tooltipString(tableItem.value.queueTime)) }} />
                }
                </span>
            </SimpleTableCell>
        );
    }

    private _renderDurationCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <span className="text-ellipsis bolt-list-cell-text">
                {
                    tableItem.value.receiveTime &&
                    <Duration startDate={tableItem.value.receiveTime} endDate={this.props.signalRState === SignalRState.Disconnected && !tableItem.value.finishTime ? new Date(Date.now()) : tableItem.value.finishTime} />
                }
                </span>
            </SimpleTableCell>
        );
    }
}
